// One chip per member shift of an Any-shift group, saying whether a caregiver
// can work that particular shift. The group-level availability columns
// collapse every shift into one value, so they cannot say which shift a
// "partial" or a booking conflict belongs to.
import { Fragment } from 'react';

import { REFERENCE_PILL_CLASS } from '@apps/web/components/ui/reference_pill';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { formatDateWithWeekday, formatTime } from '@apps/web/lib/date_utils';
import { type CalendarChipBucket } from '@apps/web/lib/shift_calendar_palette';
import { chipClassForBucket } from '@apps/web/lib/shift_reference_chip_style';
import { cn } from '@apps/web/lib/utils';
import { type ShiftAvailabilityRead } from '@libraries/typescript/api_client/schema_types';

export type RecommendationGroupShift = {
    readonly shift_id: string;
    readonly shift_start_time: string;
    readonly client_id?: string | null;
    readonly patient_name?: string | null;
};

type ShiftAvailabilityKind =
    | 'free'
    | 'partial'
    | 'booked'
    | 'unavailable'
    | 'unknown';

type ShiftAvailabilitySummary = {
    readonly kind: ShiftAvailabilityKind;
    /** Short text shown on the chip; null when the dot says it all. */
    readonly detail: string | null;
    /** Full sentence for the tooltip and screen readers. */
    readonly explanation: string;
};

// A traffic-light dot leads each pill: green can work the shift, red cannot.
const KIND_DOT_CLASSES: Record<ShiftAvailabilityKind, string> = {
    free: 'bg-green-500',
    partial: 'bg-amber-500',
    booked: 'bg-red-500',
    unavailable: 'bg-red-500',
    unknown: 'bg-muted-foreground/40',
};

// These are the chat's shift pills, so they wear the same calendar palette
// (and its dark mode); only the meaning of each tint is availability here.
const KIND_PILL_BUCKETS: Record<ShiftAvailabilityKind, CalendarChipBucket> = {
    free: 'confirmed',
    partial: 'attention',
    booked: 'open',
    unavailable: 'open',
    unknown: 'scheduled',
};

function formatRanges(
    ranges: ShiftAvailabilityRead['schedule_overlap_ranges'],
    timezone: string,
): string {
    return ranges
        .map(
            (range) =>
                `${formatTime(range.start_time, timezone)} – ${formatTime(range.end_time, timezone)}`,
        )
        .join(', ');
}

/**
 * A booking conflict outranks saved preferences: a caregiver who is already
 * working cannot take the shift, whatever hours they said they like.
 */
export function summarizeShiftAvailability(
    availability: ShiftAvailabilityRead | undefined,
    timezone: string,
): ShiftAvailabilitySummary {
    if (!availability) {
        return {
            kind: 'unknown',
            detail: null,
            explanation: 'No availability on file',
        };
    }
    if (availability.schedule_availability === 'has_shift_conflict') {
        const booked = formatRanges(
            availability.schedule_overlap_ranges,
            timezone,
        );
        return {
            kind: 'booked',
            detail: booked ? `booked ${booked}` : 'booked',
            explanation: booked
                ? `Already booked ${booked}`
                : 'Already booked during this shift',
        };
    }
    switch (availability.availability_preference) {
        case 'available':
            return {
                kind: 'free',
                detail: null,
                explanation: 'Free for the whole shift',
            };
        case 'partial': {
            const free = formatRanges(
                availability.availability_preference_overlap_ranges,
                timezone,
            );
            return {
                kind: 'partial',
                detail: free ? `${free} only` : 'part of shift',
                explanation: free
                    ? `Free for part of the shift: ${free}`
                    : 'Free for part of the shift',
            };
        }
        case 'unavailable':
            return {
                kind: 'unavailable',
                detail: 'not available',
                explanation: 'Marked unavailable in saved availability',
            };
        case 'unknown':
            return {
                kind: 'unknown',
                detail: null,
                explanation: 'No availability on file',
            };
        default:
            throw new Error(
                `Unsupported availability preference: ${String(availability.availability_preference satisfies never)}`,
            );
    }
}

export function ShiftAvailabilityChips({
    shifts,
    availabilityByShiftId,
    selectedShiftIds,
    timezone,
    onToggleShift,
    canSelectMore = true,
}: {
    readonly shifts: ReadonlyArray<RecommendationGroupShift>;
    readonly availabilityByShiftId:
        | Readonly<Record<string, ShiftAvailabilityRead>>
        | undefined;
    readonly selectedShiftIds: ReadonlySet<string>;
    readonly timezone: string;
    /** Makes each pill a toggle; omitted, the pills are display only. */
    readonly onToggleShift?: (shiftId: string, selected: boolean) => void;
    /** Whether shifts this caregiver is not yet picked for may be picked. */
    readonly canSelectMore?: boolean;
}) {
    const { formatName } = useFormatPersonName();
    // A date alone is ambiguous once the group's shifts belong to different
    // clients; a single-client group stays compact.
    const isMultiClient =
        new Set(shifts.map((shift) => shift.client_id ?? shift.patient_name))
            .size > 1;
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {shifts.map((shift) => {
                const { kind, detail, explanation } =
                    summarizeShiftAvailability(
                        availabilityByShiftId?.[shift.shift_id],
                        timezone,
                    );
                const isSelected = selectedShiftIds.has(shift.shift_id);
                const date = formatDateWithWeekday(
                    shift.shift_start_time,
                    timezone,
                );
                const chipLabel =
                    isMultiClient && shift.patient_name
                        ? `${formatName(shift.patient_name)} · ${date}`
                        : date;
                const accessibleLabel = `${chipLabel}: ${explanation}${isSelected ? ', selected' : ''}`;
                const pillClassName = cn(
                    REFERENCE_PILL_CLASS,
                    chipClassForBucket(KIND_PILL_BUCKETS[kind]),
                    // The shared pill takes its surrounding text's size;
                    // table rows stay compact.
                    'text-[13px] leading-[18px] font-normal whitespace-nowrap',
                    // The tint already means availability, so being picked
                    // is a quiet grey outline.
                    isSelected && 'ring-muted-foreground/60 ring-1',
                );
                const pillContent = (
                    <Fragment>
                        {/* An inline-flex box takes its baseline from its
                            first item; this zero-width space gives the pill
                            a text baseline (see ReferencePillContent). */}
                        <span aria-hidden="true" className="w-0 select-none">
                            {'\u200b'}
                        </span>
                        <span
                            aria-hidden="true"
                            className={cn(
                                'size-2 shrink-0 rounded-full',
                                KIND_DOT_CLASSES[kind],
                            )}
                        />
                        <span className="ml-1 min-w-0 truncate">
                            {chipLabel}
                        </span>
                        {detail ? (
                            <span className="ml-1 opacity-80">{detail}</span>
                        ) : null}
                    </Fragment>
                );
                return (
                    <Tooltip key={shift.shift_id}>
                        <TooltipTrigger asChild={true}>
                            {onToggleShift ? (
                                <button
                                    type="button"
                                    aria-pressed={isSelected}
                                    aria-label={accessibleLabel}
                                    // Same rule as the row checkbox: a
                                    // skip-listed caregiver can be unpicked
                                    // but not newly picked. (An already
                                    // contacted one gets no toggle at all.)
                                    disabled={!isSelected && !canSelectMore}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onToggleShift(
                                            shift.shift_id,
                                            !isSelected,
                                        );
                                    }}
                                    className={cn(
                                        pillClassName,
                                        'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
                                    )}
                                >
                                    {pillContent}
                                </button>
                            ) : (
                                <span
                                    aria-label={accessibleLabel}
                                    className={pillClassName}
                                >
                                    {pillContent}
                                </span>
                            )}
                        </TooltipTrigger>
                        <TooltipContent>
                            {explanation}
                            {isSelected ? ' · selected for this shift' : ''}
                            {onToggleShift
                                ? isSelected
                                    ? ' · click to remove'
                                    : ' · click to select'
                                : ''}
                        </TooltipContent>
                    </Tooltip>
                );
            })}
        </div>
    );
}
